/**
 * @file
 * @fileOverview This file contains the HandDetection class for hand gesture detection.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import * as THREE from 'three';

class HandDetection {
  constructor(onResultsCallback) {
    try {
      this.hands = new Hands({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
      });

      this.hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      this.hands.onResults((results) => {
        let landmarks = null;
        if (
          results.multiHandLandmarks &&
          results.multiHandLandmarks.length > 0
        ) {
          results.multiHandLandmarks.forEach((landmarks) => {
            const handLandmarksObject = this.getHandLandmarksObject(landmarks);
            onResultsCallback(handLandmarksObject);
            // Use handLandmarksObject for gesture recognition or other processing
          });
        }
      });

      this.videoElement = document.createElement('video');
      this.videoElement.id = 'videoElementCam';
      //this.videoElement.style.display = 'none';
      document.body.appendChild(this.videoElement);

      this.camera = new Camera(this.videoElement, {
        onFrame: async () => {
          await this.hands.send({ image: this.videoElement });
        },
        format: 'RGB',
      });

      this.canvas = document.createElement('canvas');
      this.context = this.canvas.getContext('2d');
    } catch (e) {
      console.error(e);
    }
  }

  checkForCamera() {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const videoDevices = devices.filter(
            (device) => device.kind === 'videoinput'
          );
          resolve(videoDevices.length > 0);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getVideoElement() {
    return this.videoElement;
  }

  updateCanvasDimensions() {
    this.updateCanvasDimensions();
    this.canvas.width = this.videoElement.videoWidth;
    this.canvas.height = this.videoElement.videoHeight;
  }

  getFrameData() {
    this.context.drawImage(
      this.videoElement,
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );
    const texture = new THREE.CanvasTexture(this.canvas);
    return {
      image: texture.image,
      rawData: this.canvas.toDataURL('image/jpeg'),
    };
  }

  isPinching(landmarks, fingerIndex) {
    const thumbTip = landmarks[4]; // THUMB_TIP
    const fingerTip = landmarks[fingerIndex]; // Finger tip based on index provided

    const distance = Math.sqrt(
      Math.pow(fingerTip.x - thumbTip.x, 2) +
        Math.pow(fingerTip.y - thumbTip.y, 2) +
        Math.pow(fingerTip.z - thumbTip.z, 2)
    );

    // Define a threshold distance for detecting the pinch gesture
    const threshold = 0.05; // Adjust this value as needed

    return distance < threshold;
  }

  isFunctional() {
    if (this.camera != null) {
      return true;
    } else {
      return false;
    }
  }

  // Individual functions for each finger pinch detection
  isIndexFingerPinching(landmarks) {
    return this.isPinching(landmarks, 8); // INDEX_FINGER_TIP
  }

  isMiddleFingerPinching(landmarks) {
    return this.isPinching(landmarks, 12); // MIDDLE_FINGER_TIP
  }

  isRingFingerPinching(landmarks) {
    return this.isPinching(landmarks, 16); // RING_FINGER_TIP
  }

  isPinkyFingerPinching(landmarks) {
    return this.isPinching(landmarks, 20); // PINKY_TIP
  }

  getHandLandmarksObject(landmarks) {
    const HAND_PARTS = [
      'WRIST',
      'THUMB_CMC',
      'THUMB_MCP',
      'THUMB_IP',
      'THUMB_TIP',
      'INDEX_FINGER_MCP',
      'INDEX_FINGER_PIP',
      'INDEX_FINGER_DIP',
      'INDEX_FINGER_TIP',
      'MIDDLE_FINGER_MCP',
      'MIDDLE_FINGER_PIP',
      'MIDDLE_FINGER_DIP',
      'MIDDLE_FINGER_TIP',
      'RING_FINGER_MCP',
      'RING_FINGER_PIP',
      'RING_FINGER_DIP',
      'RING_FINGER_TIP',
      'PINKY_MCP',
      'PINKY_PIP',
      'PINKY_DIP',
      'PINKY_TIP',
    ];
    if (!landmarks || landmarks.length !== 21) {
      throw new Error('Invalid landmarks data');
    }

    const handLandmarks = {};

    HAND_PARTS.forEach((part, index) => {
      const landmark = landmarks[index];
      handLandmarks[part] = {
        x: landmark.x,
        y: landmark.y,
        z: landmark.z,
      };
    });

    return {
      handparts: handLandmarks,
      indexPinch: this.isIndexFingerPinching(landmarks),
      ringFingerPinch: this.isRingFingerPinching(landmarks),
      middleFingerPinch: this.isMiddleFingerPinching(landmarks),
      pinkyFingerPinch: this.isPinkyFingerPinching(landmarks),
    };
  }

  setCustomVideoSource(videoSource) {
    this.videoElement.srcObject = videoSource;
    this.videoElement.play();
  }

  start(useCamera = true) {
    if (useCamera) {
      this.camera.start();
    } else {
      this.videoElement.play();
    }
  }

  stop() {
    this.camera.stop();
    this.videoElement.pause();
  }

  attachToWebXRSession(xrSession) {
    const mediaStream = new MediaStream();
    const videoTrack = xrSession
      .getInputSources()
      .find((source) => source.targetRayMode === 'tracked-pointer')
      .targetRaySpace.getPose().pose;
    if (videoTrack) {
      mediaStream.addTrack(videoTrack);
      this.videoElement.srcObject = mediaStream;
      this.videoElement.play();
    } else {
      console.error('No suitable video track found in the WebXR session.');
    }
  }
}

export default HandDetection;
